import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import "moment/locale/es";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { makeStyles } from "@material-ui/core/styles";
import {
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
} from "@material-ui/core";

import { EstadoVacio } from "../components/Estados";
import ModalDetalleGestion from "./ModalDetalleGestion";
import ModalGestionarSolicitud from "./ModalGestionarSolicitud";
import { useConsulta } from "../hooks/useConsulta";
import { obtenerCalendario } from "../services/bibliotecaApi";
import {
  MENSAJES,
  VISTAS,
  aEvento,
  detalleDeEvento,
  colorEnCalendario,
  estiloDeEvento,
  rangoDeVista,
} from "../model/calendario";
import { SOLICITUDES } from "../catalogo";

const localizer = momentLocalizer(moment);

const TODOS = "";
// "Mi agenda": el backend lo traduce a quien esta en sesion.
const MI_AGENDA = "yo";

const useEstilos = makeStyles({
  calendario: {
    // Los dias del mes anterior y el siguiente, apenas marcados: en el estilo
    // por defecto de react-big-calendar parecen una franja gris.
    "& .rbc-off-range-bg": { backgroundColor: "#fafafa" },
    "& .rbc-off-range": { color: "#bdbdbd" },
    "& .rbc-today": { backgroundColor: "#e3f2fd" },
  },
});

// El calendario empieza a las 6 y termina a las 22: el horario de biblioteca.
const HORA_MINIMA = moment().startOf("day").hour(6).toDate();
const HORA_MAXIMA = moment().startOf("day").hour(22).toDate();

/** Select de filtro con la opcion "Todos" al principio. */
const Filtro = ({ etiqueta, valor, opciones, onChange, todos }) => (
  <TextField
    select
    fullWidth
    size="small"
    label={etiqueta}
    value={valor}
    onChange={({ target }) => onChange(target.value)}
  >
    <MenuItem value={TODOS}>{todos}</MenuItem>
    {opciones.map(({ valor: v, nombre }) => (
      <MenuItem key={v} value={v}>
        {nombre}
      </MenuItem>
    ))}
  </TextField>
);

Filtro.propTypes = {
  etiqueta: PropTypes.string.isRequired,
  valor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  opciones: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  todos: PropTypes.string,
};

Filtro.defaultProps = { todos: "Todos" };

/**
 * Pestaña Calendario de Gestion Biblioteca, en mes, semana, dia o agenda,
 * con el color de su estado.
 *
 * Como en AGIL, el auxiliar de biblioteca solo ve su agenda: las solicitudes
 * que tiene asignadas. El administrador ve las que puede gestionar, de todos
 * o filtradas por persona.
 *
 * Mejora el de AGIL: pide al backend solo las fechas que se estan viendo (y
 * con año) y por defecto oculta las cerradas. Al pulsar una solicitud se
 * abre su detalle en un modal, el mismo de Procesos, desde donde se gestiona.
 */
const CalendarioGestion = ({ esAuxiliar, onMensaje }) => {
  const clases = useEstilos();
  const [fecha, setFecha] = useState(new Date());
  const [vista, setVista] = useState(VISTAS.MES);
  const [tipo, setTipo] = useState(TODOS);
  const [estado, setEstado] = useState(TODOS);
  // El auxiliar empieza viendo lo suyo; los demas, todo.
  const [auxiliar, setAuxiliar] = useState(esAuxiliar ? MI_AGENDA : TODOS);
  const [cerradas, setCerradas] = useState(false);
  const [abierta, setAbierta] = useState(null);
  const [gestionando, setGestionando] = useState(null);
  const [version, setVersion] = useState(0);

  const { desde, hasta } = rangoDeVista(fecha, vista);
  const { datos, cargando, error } = useConsulta(
    () => obtenerCalendario({ desde, hasta, tipo, estado, auxiliar, cerradas }),
    [desde, hasta, tipo, estado, auxiliar, cerradas, version],
    null,
  );

  const eventos = useMemo(() => (datos?.eventos ?? []).map(aEvento), [datos]);
  const estados = datos?.estados ?? [];
  const personas = datos?.auxiliares ?? [];
  const yo = datos?.yo;

  if (error && !datos) {
    return <EstadoVacio mensaje={error.message ?? "No fue posible cargar el calendario"} />;
  }

  return (
    <>
      {/* El detalle y la gestion son los mismos modales de la tabla de Procesos. */}
      {abierta && (
        <ModalDetalleGestion
          id={abierta}
          version={version}
          onCerrar={() => setAbierta(null)}
          onGestionar={(id) => {
            setAbierta(null);
            setGestionando(id);
          }}
        />
      )}
      {gestionando && (
        <ModalGestionarSolicitud
          id={gestionando}
          onMensaje={onMensaje}
          onCerrar={() => setGestionando(null)}
          onCambio={() => setVersion((actual) => actual + 1)}
        />
      )}

      <div style={{ padding: "1% 2%" }}>
        <Paper elevation={0} style={{ padding: 12, marginBottom: 8 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <Filtro
                etiqueta="Tipo"
                valor={tipo}
                onChange={setTipo}
                opciones={SOLICITUDES.map(({ codigo, nombre }) => ({ valor: codigo, nombre }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Filtro
                etiqueta="Estado"
                valor={estado}
                onChange={setEstado}
                todos={cerradas ? "Todos" : "Todos los abiertos"}
                opciones={estados.map(({ codigo, nombre }) => ({ valor: codigo, nombre }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {/* El auxiliar solo ve su agenda (el backend lo impone); el
                  administrador puede ver la de todos o la de una persona. */}
              {esAuxiliar ? (
                <TextField fullWidth size="small" label="Auxiliar" value="Mi agenda" disabled />
              ) : (
                <Filtro
                  etiqueta="Auxiliar"
                  valor={auxiliar}
                  onChange={setAuxiliar}
                  todos="Todas las solicitudes"
                  opciones={[
                    { valor: MI_AGENDA, nombre: "Mi agenda" },
                    ...personas
                      .filter(({ id }) => id !== yo)
                      .map(({ id, nombre }) => ({ valor: id, nombre })),
                  ]}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cerradas}
                    disabled={Boolean(estado)}
                    onChange={({ target }) => setCerradas(target.checked)}
                  />
                }
                label="Incluir cerradas"
              />
            </Grid>
          </Grid>
          <div style={{ marginTop: 8 }}>
            {estados.map(({ codigo, nombre }) => (
              <Chip
                key={codigo}
                size="small"
                label={nombre}
                style={{ ...colorEnCalendario(codigo), margin: 2 }}
              />
            ))}
          </div>
        </Paper>

        {cargando ? <LinearProgress /> : <div style={{ height: 4 }} />}

        <Paper elevation={0} style={{ padding: 8 }} className={clases.calendario}>
          <Calendar
            localizer={localizer}
            culture="es"
            events={eventos}
            date={fecha}
            view={vista}
            views={Object.values(VISTAS)}
            onNavigate={setFecha}
            onView={setVista}
            onSelectEvent={({ id }) => setAbierta(id)}
            eventPropGetter={estiloDeEvento}
            tooltipAccessor={detalleDeEvento}
            messages={MENSAJES}
            min={HORA_MINIMA}
            max={HORA_MAXIMA}
            popup
            style={{ height: 544 }}
          />
        </Paper>
      </div>
    </>
  );
};

CalendarioGestion.propTypes = {
  /** El auxiliar de biblioteca entra con "Mi agenda". */
  esAuxiliar: PropTypes.bool,
  onMensaje: PropTypes.func.isRequired,
};

CalendarioGestion.defaultProps = { esAuxiliar: false };

export default CalendarioGestion;
